/**
 * KeyPointsGrid — grille de cards "points clés" du profil Particulier.
 *
 * Pattern : cards avec icône en haut (halo gradient brand), titre + texte,
 * hairline accent au top, hover lift. 2 ou 3 colonnes responsives.
 *
 * Utilisé pour les sections "L'essentiel" / "Ce qu'il faut savoir" / "Critères".
 */

import { Container } from '../../layout/Container';
import { StaggerGrid, StaggerItem } from '../../motion/StaggerGrid';
import { getIcon } from '../../../lib/lucide-icons';
import { cn } from '../../../lib/cn';
import {
  PARTICULIER_GRADIENT,
  PARTICULIER_SECTION,
  type ParticulierKeyPoint,
} from '../../../config/audience/particulier.ui';
import { ParticulierSectionHeader } from './SectionHeader';

export interface ParticulierKeyPointsGridProps {
  /** Eyebrow + titre + description de la section */
  eyebrow: string;
  title: string;
  description?: string;
  /** Liste des points clés */
  points: ParticulierKeyPoint[];
  /** Nombre de colonnes au breakpoint lg (2 par défaut) */
  cols?: 2 | 3;
  /** Alignement du header (centré par défaut) */
  align?: 'left' | 'center';
  /** Surcharger les classes de section */
  sectionClass?: string;
}

export function ParticulierKeyPointsGrid({
  eyebrow,
  title,
  description,
  points,
  cols = 2,
  align = 'center',
  sectionClass,
}: ParticulierKeyPointsGridProps) {
  const gridCols = cols === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2';

  return (
    <section className={cn(PARTICULIER_SECTION.standard, sectionClass)}>
      <Container>
        <ParticulierSectionHeader
          eyebrow={eyebrow}
          title={title}
          description={description}
          align={align}
        />

        <StaggerGrid className={cn('grid gap-4', gridCols)}>
          {points.map((point) => {
            const Icon = getIcon(point.iconName);
            return (
              <StaggerItem key={point.label}>
                <div className="group relative h-full overflow-hidden rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_12px_36px_-12px_rgba(10,10,10,.10)]">
                  <div
                    aria-hidden
                    className={cn(
                      'absolute inset-x-0 top-0 h-px',
                      PARTICULIER_GRADIENT.cardTopHairline,
                    )}
                  />
                  <span
                    className={cn(
                      'mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl',
                      PARTICULIER_GRADIENT.iconHalo,
                      'text-brand-700 ring-1 ring-inset ring-brand-200/60 transition-transform duration-200 ease-out group-hover:scale-105',
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <p className="font-display text-base font-semibold text-ink">{point.label}</p>
                  <p className="mt-1.5 text-[13.5px] leading-relaxed text-stone-600">
                    {point.description}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGrid>
      </Container>
    </section>
  );
}
